import React, { useState } from "react";
import { Outlet, NavLink, useNavigate, Link } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { auth } from "../../firebase";
import NotificationButton from "../notifications/NotificationButton";
import BottomBar from "../../components/layout/BottomBar";

import {
  Bars3Icon,
  BellIcon,
  CalendarIcon,
  FolderIcon,
  HomeIcon,
  UserIcon,
  ArrowRightOnRectangleIcon,
  ShieldCheckIcon,
  TicketIcon,
  ChatBubbleLeftRightIcon,
  QuestionMarkCircleIcon,
} from "@heroicons/react/24/outline";
import logo from "../../assets/wattlenest.png";
import logoWhite from "../../assets/wattlenest-white.png";

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(" ");
}

const PortalLayout: React.FC = () => {
  const navigate = useNavigate();
  const { isAdmin, isApproved } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Define navigation items based on approval status
  const getNavItems = () => {
    const baseItems = [
      { name: "Dashboard", path: "/portal/dashboard", icon: HomeIcon },
      { name: "Profile", path: "/portal/profile", icon: UserIcon },
    ];

    // Only show additional items if user is approved or admin
    if (isApproved || isAdmin) {
      return [
        ...baseItems,
        { name: "Events", path: "/portal/events", icon: CalendarIcon },
        { name: "My Tickets", path: "/portal/tickets", icon: TicketIcon },
        { name: "Resources", path: "/portal/resources", icon: FolderIcon },
        {
          name: "Share Feedback",
          path: "/portal/feedback",
          icon: ChatBubbleLeftRightIcon,
        },
      ];
    }

    return baseItems;
  };

  const navItems = getNavItems();

  const userNavigation = [
    { name: "Your profile", path: "/portal/profile" },
    { name: "Sign out", path: "#" },
  ];

  const handleSignOut = async () => {
    try {
      await auth.signOut();
      navigate("/");
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  return (
    <div>
      {/* Static sidebar for desktop */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-72 lg:flex-col">
        <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-gold px-6 pb-4">
          <div className="flex shrink-0 items-center">
            <img alt="Wattlenest" src={logoWhite} className="w-full" />
          </div>
          <nav className="flex flex-1 flex-col">
            <ul className="flex flex-1 flex-col gap-y-7">
              <li>
                <ul className="-mx-2 space-y-1">
                  {navItems.map((item) => (
                    <li key={item.name}>
                      <NavLink
                        to={item.path}
                        className={({ isActive }) =>
                          classNames(
                            isActive
                              ? "bg-goldlight text-gray-800"
                              : "text-white hover:bg-white hover:text-gray-800",
                            "group flex gap-x-3 rounded-md p-2 text-sm font-semibold"
                          )
                        }
                      >
                        <item.icon
                          className={classNames("h-6 w-6 shrink-0")}
                          aria-hidden="true"
                        />
                        {item.name}
                      </NavLink>
                    </li>
                  ))}
                </ul>
              </li>
              <li className="mt-auto">
                <ul className="-mx-2 space-y-1">
                  {isAdmin && (
                    <li>
                      <NavLink
                        to="/portal/admin"
                        className={({ isActive }) =>
                          classNames(
                            isActive
                              ? "bg-white text-gray-800"
                              : "text-gray-800 hover:bg-white hover:text-gray-800",
                            "group flex gap-x-3 rounded-md p-2 text-sm font-semibold"
                          )
                        }
                      >
                        <ShieldCheckIcon
                          className="h-6 w-6 shrink-0"
                          aria-hidden="true"
                        />
                        Admin
                      </NavLink>
                    </li>
                  )}
                  <li>
                    <button
                      onClick={handleSignOut}
                      className="group flex gap-x-3 rounded-md p-2 text-sm font-semibold text-gray-200 hover:bg-white hover:text-gray-800 w-full"
                    >
                      <ArrowRightOnRectangleIcon
                        className="h-6 w-6 shrink-0"
                        aria-hidden="true"
                      />
                      Sign Out
                    </button>
                  </li>
                </ul>
              </li>
            </ul>
          </nav>
        </div>
      </div>

      <div className="lg:pl-72">
        {/* Top navigation */}
        <div className="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-4 border-b border-gray-200 bg-white px-4 shadow-sm sm:gap-x-6 sm:px-6 lg:px-8">
          <img alt="Wattlenest" src={logo} className="h-12 w-auto sm:hidden" />

          <div className="flex flex-1 gap-x-4 self-stretch lg:gap-x-6">
            <div className="flex-1" /> {/* Spacer */}
            <div className="flex items-center gap-x-2 lg:gap-x-6">
              <NotificationButton />
              {(isApproved || isAdmin) && (
                <Link
                  to="/portal/feedback"
                  className="p-2 rounded-full text-gray-700 hover:text-gray-900 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gold transition-colors"
                  title="Send Feedback"
                  aria-label="Send Feedback"
                >
                  <QuestionMarkCircleIcon
                    className="h-6 w-6"
                    aria-hidden="true"
                  />
                </Link>
              )}
            </div>
          </div>
        </div>

        <main className="min-h-screen py-10 bg-gray-100 ">
          <div className="px-4 sm:px-6 lg:px-8">
            <Outlet />
          </div>
        </main>
      </div>
      <BottomBar></BottomBar>
    </div>
  );
};

export default PortalLayout;
